import React, { useState, useEffect, useRef } from 'react';

export interface DevDimensions {
  codebaseContext:        { complete: boolean; summary: string | null; skipped: boolean };
  implementationApproach: { complete: boolean; summary: string | null; skipped: boolean; adrConflicts: string[] };
  acceptanceCriteria:     { complete: boolean; summary: string | null; skipped: boolean; edgeCases: string[] };
}

interface DimensionProgressProps {
  dimensions: DevDimensions;
  promptGenerationUnlocked: boolean;
  generating?: boolean;
  onGeneratePrompt?: () => void;
}

interface PillDef {
  key: keyof DevDimensions;
  label: string;
}

const PILLS: PillDef[] = [
  { key: 'codebaseContext',        label: 'Codebase' },
  { key: 'implementationApproach', label: 'Approach' },
  { key: 'acceptanceCriteria',     label: 'AC Check' },
];

type PillState = 'complete' | 'skipped' | 'active' | 'pending';

function getPillState(
  key: keyof DevDimensions,
  dim: DevDimensions[typeof key],
  activeDimension: keyof DevDimensions | null,
): PillState {
  if (dim.complete && !dim.skipped) return 'complete';
  if (dim.skipped) return 'skipped';
  if (key === activeDimension) return 'active';
  return 'pending';
}

const PILL_STYLES: Record<PillState, React.CSSProperties> = {
  complete: {
    background: 'rgba(34,197,94,0.15)',
    color: '#16a34a',
    border: '1px solid rgba(34,197,94,0.35)',
  },
  skipped: {
    background: 'rgba(245,158,11,0.10)',
    color: 'var(--warning)',
    border: '1px solid rgba(245,158,11,0.3)',
  },
  active: {
    background: 'rgba(124,58,237,0.10)',
    color: 'var(--primary)',
    border: '1px solid rgba(124,58,237,0.35)',
  },
  pending: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  },
};

const PILL_ICON: Record<PillState, string> = {
  complete: '✓',
  skipped:  '↷',
  active:   '●',
  pending:  '○',
};

export default function DimensionProgress({
  dimensions,
  promptGenerationUnlocked,
  generating,
  onGeneratePrompt,
}: DimensionProgressProps) {
  const [justUnlocked, setJustUnlocked] = useState(false);
  const prevUnlocked = useRef(promptGenerationUnlocked);

  useEffect(() => {
    // Only pulse when transitioning from false → true
    if (promptGenerationUnlocked && !prevUnlocked.current) {
      const startTimer = setTimeout(() => setJustUnlocked(true), 0);
      const stopTimer = setTimeout(() => setJustUnlocked(false), 3000);
      prevUnlocked.current = promptGenerationUnlocked;
      return () => { clearTimeout(startTimer); clearTimeout(stopTimer); };
    }
    prevUnlocked.current = promptGenerationUnlocked;
  }, [promptGenerationUnlocked]);

  // First incomplete, non-skipped dimension is "active"
  const activeDimension: keyof DevDimensions | null = (() => {
    for (const { key } of PILLS) {
      const d = dimensions[key];
      if (!d.complete && !d.skipped) return key;
    }
    return null;
  })();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 16px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface)',
        flexShrink: 0,
        flexWrap: 'wrap',
      }}
    >
      {justUnlocked && (
        <style>{`
          @keyframes bp-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.85;transform:scale(1.04)} }
          .bp-pulse-anim { animation: bp-pulse 0.8s ease-in-out 3; }
        `}</style>
      )}
      <span
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          flexShrink: 0,
        }}
      >
        Dimensions:
      </span>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {PILLS.map(({ key, label }) => {
          const state = getPillState(key, dimensions[key], activeDimension);
          return (
            <span
              key={key}
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 12,
                fontWeight: 500,
                padding: '3px 9px',
                borderRadius: 'var(--radius-sm)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                ...PILL_STYLES[state],
              }}
            >
              <span style={{ fontSize: 10 }}>{PILL_ICON[state]}</span>
              {label}
            </span>
          );
        })}
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        {promptGenerationUnlocked ? (
          <button
            className={justUnlocked ? 'bp-pulse-anim' : ''}
            onClick={onGeneratePrompt}
            disabled={generating}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 12px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--primary)',
              color: 'white',
              cursor: generating ? 'not-allowed' : 'pointer',
              opacity: generating ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            {generating ? 'Generating...' : '🔓 Ready to generate'}
          </button>
        ) : (
          <span
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              color: 'var(--text-secondary)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            🔒 Prompt locked
          </span>
        )}
      </div>
    </div>
  );
}
